import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconButton, Tooltip } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import { useAppStore } from '@/store/appStore'
import { useContentScrollArea } from '@/hooks/useContentScrollArea'
import { useContentBundle } from '@/hooks/useContentBundle'
import { GlossaryPage } from '@/pages/GlossaryPage'
import { AppendicesPage } from '@/pages/AppendicesPage'
import { CrosswalksPage } from '@/pages/CrosswalksPage'
import { ReleaseNotesPage } from '@/pages/ReleaseNotesPage'
import { OverviewPage } from '@/pages/OverviewPage'
import { UserContentPage } from '@/pages/UserContentPage'
import { FaqPage } from '@/pages/FaqPage'
import { MediaGalleryPage } from '@/pages/MediaGalleryPage'

const overlayBtnSx = {
  color: 'var(--color-on-dark-75)',
  border: '1px solid var(--color-on-dark-50)',
  borderRadius: '4px',
  width: 32,
  height: 32,
  '&:hover': {
    background: 'var(--color-on-dark-15)',
    color: 'white',
  },
}

const TITLE_KEYS: Record<string, string> = {
  overview: 'pages.overview',
  appendices: 'pages.appendices',
  glossary: 'pages.glossary',
  'release-notes': 'pages.releaseNotes',
  'user-content': 'pages.userContent',
  faq: 'pages.faq',
  crosswalks: 'pages.crosswalks',
  'media-gallery': 'mediaGallery.title',
}

export function PageOverlay() {
  const { t } = useTranslation()
  const { overlayPage, setOverlayPage } = useAppStore()
  const { data: bundle } = useContentBundle()
  const galleryDisabled = overlayPage?.type === 'media-gallery' && bundle !== undefined && !bundle.mediaGalleryEnabled
  const bodyRef = useRef<HTMLDivElement>(null)
  // Focus the overlay body as it opens so page keys scroll it without a click first
  useContentScrollArea(bodyRef, overlayPage?.type ?? null)
  const [canScrollTop, setCanScrollTop] = useState(false)
  const [maximized, setMaximized] = useState(false)

  useEffect(() => {
    if (galleryDisabled) setOverlayPage(null)
  }, [galleryDisabled, setOverlayPage])

  useEffect(() => {
    setCanScrollTop(false)
    const el = bodyRef.current
    if (!el) return
    const onScroll = () => setCanScrollTop(el.scrollTop > 100)
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [overlayPage])

  // Lock body scroll while open; restore on close
  useEffect(() => {
    if (overlayPage) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [overlayPage])

  // Close on Escape
  useEffect(() => {
    if (!overlayPage) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOverlayPage(null)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [overlayPage, setOverlayPage])

  // After the overlay opens, scroll to hash if present
  useEffect(() => {
    if (!overlayPage?.hash || !bodyRef.current) return
    const container = bodyRef.current
    const tryScroll = (attemptsLeft: number) => {
      const el = container.querySelector(overlayPage.hash!)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        el.classList.add('glossary-term-highlight')
        setTimeout(() => el.classList.remove('glossary-term-highlight'), 2000)
      } else if (attemptsLeft > 0) {
        setTimeout(() => tryScroll(attemptsLeft - 1), 150)
      }
    }
    tryScroll(10)
  }, [overlayPage])

  if (!overlayPage) return null

  const titleKey = TITLE_KEYS[overlayPage.type]
  const title = titleKey ? t(titleKey) : ''

  return (
    <div
      className="page-overlay-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => {
        // Close when clicking directly on the backdrop (not the panel)
        if (e.target === e.currentTarget) setOverlayPage(null)
      }}
    >
        <div className={`page-overlay-panel${overlayPage.type !== 'glossary' ? ' page-overlay-panel--wide' : ''}${maximized ? ' page-overlay-panel--maximized' : ''}`} data-page={overlayPage.type}>
        <div className="page-overlay-header">
          <span className="page-overlay-title">{title}</span>
          <div className="page-overlay-header-actions">
            {canScrollTop && (
              <Tooltip title={t('pageOverlay.scrollToTop')}>
                <IconButton
                  onClick={() => bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
                  aria-label={t('pageOverlay.scrollToTop')}
                  size="small"
                  sx={overlayBtnSx}
                >
                  <KeyboardArrowUpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={maximized ? t('pageOverlay.restore') : t('pageOverlay.maximize')}>
              <IconButton
                className="page-overlay-maximize-btn"
                onClick={() => setMaximized((m) => !m)}
                aria-label={maximized ? t('pageOverlay.restore') : t('pageOverlay.maximize')}
                aria-pressed={maximized}
                size="small"
                sx={overlayBtnSx}
              >
                {maximized ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <IconButton
              onClick={() => setOverlayPage(null)}
              aria-label={t('pageOverlay.close')}
              title={t('pageOverlay.closeEsc')}
              size="small"
              sx={overlayBtnSx}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
        <div className={`page-overlay-body${overlayPage.type === 'appendices' || overlayPage.type === 'crosswalks' || overlayPage.type === 'media-gallery' ? ' page-overlay-body--split' : ''}`} ref={bodyRef} tabIndex={0}>
          {overlayPage.type === 'glossary' && <GlossaryPage />}
          {overlayPage.type === 'appendices' && <AppendicesPage />}
          {overlayPage.type === 'release-notes' && <ReleaseNotesPage />}
          {overlayPage.type === 'overview' && <OverviewPage />}
          {overlayPage.type === 'user-content' && <UserContentPage />}
          {overlayPage.type === 'faq' && <FaqPage />}
          {overlayPage.type === 'crosswalks' && <CrosswalksPage />}
          {overlayPage.type === 'media-gallery' && !galleryDisabled && <MediaGalleryPage />}
        </div>
      </div>
    </div>
  )
}
